/**
 * Copy text to the clipboard, with a fallback for when the Clipboard API is blocked.
 *
 * The async Clipboard API is gated by the `clipboard-write` permission policy, and a
 * permission policy is **inherited by nested frames**: a value of `(self)` allows the
 * top document's own origin and nothing else, so a cross-origin iframe is excluded.
 * The `allow="clipboard-write"` attribute on an iframe can only *request* what the
 * parent already permits -- the parent document is the ceiling, not the attribute.
 *
 * The MBOA portal sends `clipboard-write=(self)`, so inside the portal embed
 * `navigator.clipboard.writeText` rejects. Before this module the rejection was
 * swallowed into `console.error`, which meant the copy button in the portal was a
 * silent no-op: the user clicked and nothing happened, with no feedback (MA-228).
 *
 * `document.execCommand("copy")` is the fallback because it is governed by user
 * activation rather than by the permission policy, so it still works in the frame.
 * It is deprecated and it is here on purpose -- for this case there is no
 * non-deprecated equivalent, and the alternative is a button that does nothing.
 */

/**
 * Copy via a temporary textarea and `execCommand`. Returns whether it worked.
 *
 * The textarea has to be selectable, which rules out `display: none` and
 * `visibility: hidden` -- an unrenderable element cannot hold a selection, and the
 * copy silently does nothing. It is therefore positioned off-screen instead.
 *
 * Whatever the user had selected is restored afterwards, since selecting our own
 * element to run the command would otherwise clear their selection as a side effect.
 */
function copyViaSelection(text: string): boolean {
  const area = document.createElement("textarea");
  area.value = text;
  area.setAttribute("readonly", "");
  area.style.position = "fixed";
  area.style.top = "-9999px";
  area.style.left = "-9999px";
  area.style.opacity = "0";

  document.body.appendChild(area);

  const selection = document.getSelection();
  const previous =
    selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

  let copied = false;
  try {
    area.select();
    copied = document.execCommand("copy");
  } catch {
    copied = false;
  } finally {
    area.remove();
    if (previous && selection) {
      selection.removeAllRanges();
      selection.addRange(previous);
    }
  }

  return copied;
}

/**
 * Put `text` on the clipboard. Resolves to whether it actually got there.
 *
 * Never rejects: the caller's job is to tell the user whether it worked, and a
 * thrown error and a `false` return would mean the same thing to it.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    // `navigator.clipboard` is undefined outside a secure context, so this is a
    // capability check and not just defensive typing.
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Blocked by a permission policy, or refused for lack of user activation.
    // Either way the fallback is worth trying.
  }

  return copyViaSelection(text);
}
